import { CAT_FOOD_PRICE, CROPS } from "../data/crops";
import { CAT_IDS, CATS } from "../data/cats";
import { DECORATIONS } from "../data/decorations";
import { clearPlot, getCropStage, plantCrop, waterCrop } from "../systems/CropSystem";
import type {
  CatId,
  CropId,
  DecorationState,
  DecorationType,
  GameState,
  PlayerState,
} from "../types";
import { createDefaultState } from "./defaultState";

type StoreChangeDetail = { state: GameState; reason: string };

export class GameStore extends EventTarget {
  readonly storageKey = "cat-town-alpha-v2";
  private state: GameState;
  private readonly storage: Storage | null;

  constructor(storage?: Storage | null) {
    super();
    this.storage = storage === undefined
      ? (typeof window !== "undefined" ? window.localStorage : null)
      : storage;
    this.state = this.load();
    this.applyOfflineDecay();
    this.persist();
  }

  getSnapshot(): GameState {
    return structuredClone(this.state);
  }

  subscribe(handler: (detail: StoreChangeDetail) => void): () => void {
    const listener = (event: Event) => handler((event as CustomEvent<StoreChangeDetail>).detail);
    this.addEventListener("change", listener);
    return () => this.removeEventListener("change", listener);
  }

  plant(plotId: string, cropId: CropId, now = Date.now()): void {
    this.update("plant", (state) => {
      const plot = state.plots.find((entry) => entry.id === plotId);
      if (!plot) throw new Error("ไม่พบแปลงผัก");
      if (state.inventory.seeds[cropId] <= 0) throw new Error("เมล็ดพันธุ์ไม่พอ");
      Object.assign(plot, plantCrop(plot, cropId, now));
      state.inventory.seeds[cropId] -= 1;
    });
  }

  water(plotId: string, now = Date.now()): void {
    this.update("water", (state) => {
      const plot = state.plots.find((entry) => entry.id === plotId);
      if (!plot) throw new Error("ไม่พบแปลงผัก");
      Object.assign(plot, waterCrop(plot, now));
    });
  }

  harvest(plotId: string, now = Date.now()): void {
    this.update("harvest", (state) => {
      const plot = state.plots.find((entry) => entry.id === plotId);
      if (!plot || !plot.cropId) throw new Error("ไม่มีผลผลิตให้เก็บ");
      if (getCropStage(plot, now) !== "ready") throw new Error("ผักยังโตไม่เต็มที่");
      const crop = CROPS[plot.cropId];
      state.inventory.produce[plot.cropId] += crop.harvestAmount;
      state.exp += crop.exp;
      this.applyLevelUps(state);
      Object.assign(plot, clearPlot(plot));
    });
  }

  buySeed(cropId: CropId, quantity = 1): void {
    const price = CROPS[cropId].seedPrice * quantity;
    this.update("buy-seed", (state) => {
      if (state.coins < price) throw new Error("เหรียญไม่พอ");
      state.coins -= price;
      state.inventory.seeds[cropId] += quantity;
    });
  }

  sellProduce(cropId: CropId, quantity = 1): void {
    this.update("sell-produce", (state) => {
      if (state.inventory.produce[cropId] < quantity) throw new Error("ผลผลิตไม่พอขาย");
      state.inventory.produce[cropId] -= quantity;
      state.coins += CROPS[cropId].sellPrice * quantity;
    });
  }

  buyCatFood(quantity = 1): void {
    const price = CAT_FOOD_PRICE * quantity;
    this.update("buy-food", (state) => {
      if (state.coins < price) throw new Error("เหรียญไม่พอ");
      state.coins -= price;
      state.inventory.catFood += quantity;
    });
  }

  createPlayer(appearance: Pick<PlayerState, "displayName" | "bodyType" | "hairStyle" | "hairColor" | "outfit" | "outfitColor">): void {
    this.update("create-player", (state) => {
      state.player = {
        ...state.player,
        ...appearance,
        displayName: appearance.displayName.trim().slice(0, 18) || "ชาวสวน",
        created: true,
      };
    });
  }

  buyCat(catId: CatId, now = Date.now()): void {
    const definition = CATS[catId];
    this.update("buy-cat", (state) => {
      const cat = state.cats.find((entry) => entry.id === catId);
      if (!cat) throw new Error("ไม่พบแมวตัวนี้ในศูนย์รับเลี้ยง");
      if (cat.owned) throw new Error(`${cat.name} อยู่ในครอบครัวแล้ว`);
      if (state.coins < definition.price) throw new Error(`ต้องการอีก ${definition.price - state.coins} เหรียญ`);
      state.coins -= definition.price;
      cat.owned = true;
      cat.hunger = 82;
      cat.happiness = 78;
      cat.behavior = "happy";
      cat.location = "farm";
      cat.lastCareAt = now;
      state.exp += 15;
      this.applyLevelUps(state);
    });
  }

  feedCat(catId: CatId, now = Date.now()): void {
    this.update("feed-cat", (state) => {
      const cat = state.cats.find((entry) => entry.id === catId);
      if (!cat?.owned) throw new Error("ยังไม่ได้รับเลี้ยงแมวตัวนี้");
      if (state.inventory.catFood <= 0) throw new Error("อาหารแมวหมดแล้ว");
      state.inventory.catFood -= 1;
      cat.hunger = Math.min(100, cat.hunger + 28);
      cat.happiness = Math.min(100, cat.happiness + 8);
      cat.behavior = "eat";
      cat.lastCareAt = now;
      state.exp += 3;
      this.applyLevelUps(state);
    });
  }

  petCat(catId: CatId, now = Date.now()): void {
    this.update("pet-cat", (state) => {
      const cat = state.cats.find((entry) => entry.id === catId);
      if (!cat?.owned) throw new Error("ยังไม่ได้รับเลี้ยงแมวตัวนี้");
      cat.happiness = Math.min(100, cat.happiness + 18);
      cat.hunger = Math.max(0, cat.hunger - 3);
      cat.behavior = "pet";
      cat.lastCareAt = now;
      state.exp += 2;
      this.applyLevelUps(state);
    });
  }

  playWithCat(catId: CatId, now = Date.now()): void {
    this.petCat(catId, now);
  }

  buyDecoration(type: DecorationType): void {
    const price = DECORATIONS[type].price;
    this.update("buy-decoration", (state) => {
      if (state.coins < price) throw new Error("เหรียญไม่พอ");
      state.coins -= price;
      state.inventory.decorations[type] += 1;
    });
  }

  placeDecoration(type: DecorationType, cellX: number, cellY: number): DecorationState {
    let placed!: DecorationState;
    this.update("place-decoration", (state) => {
      if (state.inventory.decorations[type] <= 0) throw new Error("ของตกแต่งชิ้นนี้หมดแล้ว");
      placed = {
        id: "decor-" + Date.now() + "-" + Math.random().toString(16).slice(2),
        type,
        cellX,
        cellY,
        rotation: 0,
      };
      state.inventory.decorations[type] -= 1;
      state.decorations.push(placed);
    });
    return placed;
  }

  moveDecoration(id: string, cellX: number, cellY: number): void {
    this.update("move-decoration", (state) => {
      const decoration = state.decorations.find((entry) => entry.id === id);
      if (!decoration) throw new Error("ไม่พบของตกแต่ง");
      decoration.cellX = cellX;
      decoration.cellY = cellY;
    });
  }

  storeDecoration(id: string): void {
    this.update("store-decoration", (state) => {
      const index = state.decorations.findIndex((entry) => entry.id === id);
      if (index < 0) throw new Error("ไม่พบของตกแต่ง");
      const [decoration] = state.decorations.splice(index, 1);
      if (!decoration) return;
      state.inventory.decorations[decoration.type] += 1;
    });
  }

  updateCatPosition(catId: CatId, cellX: number, cellY: number, behavior: GameState["cats"][number]["behavior"]): void {
    this.update("cat-position", (state) => {
      const cat = state.cats.find((entry) => entry.id === catId);
      if (!cat?.owned) return;
      cat.cellX = cellX;
      cat.cellY = cellY;
      cat.behavior = behavior;
    }, false, false);
  }

  updatePlayerPosition(cellX: number, cellY: number, direction: PlayerState["direction"]): void {
    this.update("player-position", (state) => {
      state.player.cellX = cellX;
      state.player.cellY = cellY;
      state.player.direction = direction;
    }, false, false);
  }

  tick(now = Date.now()): void {
    const elapsedMinutes = Math.floor((now - this.state.lastUpdatedAt) / 60_000);
    if (elapsedMinutes <= 0) return;
    this.update("tick", (state) => {
      for (const cat of state.cats.filter((entry) => entry.owned)) {
        cat.hunger = Math.max(15, cat.hunger - elapsedMinutes);
        cat.happiness = Math.max(20, cat.happiness - Math.floor(elapsedMinutes / 2));
      }
      state.lastUpdatedAt = now;
    });
  }

  private update(
    reason: string,
    mutator: (draft: GameState) => void,
    notify = true,
    touchUpdatedAt = true,
  ): void {
    const draft = structuredClone(this.state);
    mutator(draft);
    draft.coins = Math.max(0, Math.floor(draft.coins));
    if (touchUpdatedAt) draft.lastUpdatedAt = Date.now();
    this.state = draft;
    this.persist();
    if (notify) {
      this.dispatchEvent(new CustomEvent<StoreChangeDetail>("change", {
        detail: { state: this.getSnapshot(), reason },
      }));
    }
  }

  private applyLevelUps(state: GameState): void {
    let needed = state.level * 30;
    while (state.exp >= needed) {
      state.exp -= needed;
      state.level += 1;
      state.coins += 20 + state.level * 5;
      needed = state.level * 30;
    }
  }

  private load(): GameState {
    try {
      const raw = this.storage?.getItem(this.storageKey);
      if (!raw) return createDefaultState();
      const parsed = JSON.parse(raw) as Partial<GameState>;
      if (parsed.schemaVersion !== 2) return createDefaultState();
      return this.validate(parsed as GameState);
    } catch (error) {
      console.warn("[CatTown] Save data could not be loaded; using defaults", error);
      return createDefaultState();
    }
  }

  private validate(candidate: GameState): GameState {
    const fallback = createDefaultState();
    if (!Array.isArray(candidate.plots) || !Array.isArray(candidate.cats)) return fallback;
    const candidateCats = new Map(candidate.cats.map((cat) => [cat.id, cat]));
    return {
      ...fallback,
      ...candidate,
      coins: Number.isFinite(candidate.coins) ? Math.max(0, candidate.coins) : fallback.coins,
      player: { ...fallback.player, ...candidate.player },
      inventory: {
        seeds: { ...fallback.inventory.seeds, ...candidate.inventory?.seeds },
        produce: { ...fallback.inventory.produce, ...candidate.inventory?.produce },
        catFood: Math.max(0, candidate.inventory?.catFood ?? fallback.inventory.catFood),
        decorations: {
          ...fallback.inventory.decorations,
          ...candidate.inventory?.decorations,
        },
      },
      cats: CAT_IDS.map((id) => ({ ...fallback.cats.find((cat) => cat.id === id)!, ...candidateCats.get(id) })),
      ownedFurniture: Array.isArray(candidate.ownedFurniture) ? candidate.ownedFurniture : fallback.ownedFurniture,
    };
  }

  private applyOfflineDecay(): void {
    const now = Date.now();
    const elapsedMinutes = Math.min(360, Math.floor((now - this.state.lastUpdatedAt) / 60_000));
    if (elapsedMinutes <= 0) return;
    for (const cat of this.state.cats.filter((entry) => entry.owned)) {
      cat.hunger = Math.max(15, cat.hunger - elapsedMinutes);
      cat.happiness = Math.max(20, cat.happiness - Math.floor(elapsedMinutes / 2));
    }
    this.state.lastUpdatedAt = now;
  }

  private persist(): void {
    try {
      this.storage?.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (error) {
      console.error("[CatTown] Save failed", error);
    }
  }
}
